import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Typography,
  Grid,
  Tooltip,
  Chip,
  IconButton,
  TextField,
  Button,
  ClickAwayListener,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PublicIcon from '@mui/icons-material/Public';
import CustomDropdown from '../../../components/CustomDropdownWithPlus';
import SupplierSearchSelectAsync from '../../SupplierSearchSelectAsync';
import CustomDatePicker from '../../CustomDatePicker';
import CustomTextField from '../../CustomTextField';
import TextArea from '../../TextArea';
import styles from './index.module.css';
import BillingItemsTable, { includedBillingItems } from './BillingItemsTable';
import "./MainBillingForm.css";

import {
  computeBillTotalsCents,
  fromCents,
  formatCAD,
  toCents,
} from '../../../utils/billingTotals';
import {
  applyBillingSupplierSearchSelection,
  supplierSourceToSearchTab,
} from './billingSupplierIdentity';

const InfoDot = ({ title, sx }) => (
  <Tooltip title={title} arrow>
    <span style={{ display: 'inline-flex', alignItems: 'center', ...sx }}>
      <InfoOutlinedIcon sx={{ fontSize: 14, ml: 0.5 }} />
    </span>
  </Tooltip>
);

const ClickHelpDot = ({
  id,
  title,
  label,
  openHelpTooltip,
  setOpenHelpTooltip,
}) => (
  <ClickAwayListener
    onClickAway={() => {
      if (openHelpTooltip === id) setOpenHelpTooltip(null);
    }}
  >
    <Tooltip
      title={title}
      arrow
      open={openHelpTooltip === id}
      disableHoverListener
      disableFocusListener
      disableTouchListener
      onClose={() => setOpenHelpTooltip(null)}
    >
      <IconButton
        size="small"
        aria-label={`About ${label}`}
        aria-expanded={openHelpTooltip === id}
        onClick={(event) => {
          event.stopPropagation();
          setOpenHelpTooltip(openHelpTooltip === id ? null : id);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setOpenHelpTooltip(null);
        }}
        sx={{ p: 0.25, ml: 0.25 }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Tooltip>
  </ClickAwayListener>
);

const findOptionLabel = (options = [], value) => {
  if (value == null) return '';

  const strVal = String(value);

  const hit = options.find(
    (o) => String(o?.id ?? o?.value) === strVal
  );

  return hit?.name || hit?.label || '';
};

export const savedBillPreTaxSubtotalCents = (bill) => {
  if (!bill) return 0;

  const savedSubTotal = Number(bill?.subTotal);
  if (
    bill?.subTotal !== null &&
    bill?.subTotal !== undefined &&
    bill?.subTotal !== '' &&
    Number.isFinite(savedSubTotal)
  ) {
    return toCents(savedSubTotal);
  }

  const tC = computeBillTotalsCents(bill || {});
  if (tC.subTotalCents !== 0) {
    return tC.subTotalCents;
  }

  return 0;
};

export const savedBillPreTaxSubtotal = (bill) =>
  fromCents(savedBillPreTaxSubtotalCents(bill));

export const calculatePoReconciliation = ({
  poAmount = 0,
  poGroupBills = [],
  currentBillId = null,
  liveSubTotal = 0,
} = {}) => {
  const targetCents = toCents(poAmount);
  const savedGroupTotalCents = Array.isArray(poGroupBills)
    ? poGroupBills.reduce(
        (sum, bill) => sum + savedBillPreTaxSubtotalCents(bill),
        0
      )
    : 0;
  const currentSavedAmountCents = currentBillId && Array.isArray(poGroupBills)
    ? savedBillPreTaxSubtotalCents(
        poGroupBills.find((bill) => String(bill?.id) === String(currentBillId))
      )
    : 0;
  const projectedGroupTotalCents = Math.max(
    0,
    savedGroupTotalCents - currentSavedAmountCents + toCents(liveSubTotal)
  );
  const varianceCents = projectedGroupTotalCents - targetCents;
  const pctOfTarget = targetCents > 0
    ? (projectedGroupTotalCents / targetCents) * 100
    : projectedGroupTotalCents > 0
      ? 200
      : 0;

  return {
    target: fromCents(targetCents),
    savedGroupTotal: fromCents(savedGroupTotalCents),
    currentSavedAmount: fromCents(currentSavedAmountCents),
    projectedGroupTotal: fromCents(projectedGroupTotalCents),
    variance: fromCents(varianceCents),
    pctOfTarget,
  };
};

const BILL_PARTY_MODES = [
  'supplier',
  'decorator',
  'shipping-account',
];

const MainBillingForm = ({
  formData,
  handleSelect,
  handleDateChange,
  handleInputChange,
  selectedSupplierObj,
  onSelectSupplier,
  instanceUniqueId,
  decoratorOptions = [],
  shippingAccountOptions = [],
  purchaseOrderOptions = [],
  isPoOptionalSupplier = false,
  poOptionalSupplierLoading = false,
  taxOptions,
  singleBillData,
  setSingleBillData,
  financeCodes = [],
  tabId,
  poAmount = 0,
  poGroupBills = [],
  currentBillId = null,
  poSupplierName,
  poDecoratorName,
  poShippingAccountName,
  poNumber,
  isDecoratorPo = false,
  isMinimalBillEntryActive = false,
  minimalBillEntryMessage = "",
  billTaxInput,
  setBillTaxInput,
  onBillTaxInputChange = setBillTaxInput,
  onResetBillTaxInput = () => {},
  billTaxManuallyEdited = false,
  hasLegacyTaxDifference = false,
  clearLegacyTaxDifference,
  allowNegativeBillTax = false,
  allowDetailedItemTaxCustomization = true,
  fieldErrors = {},
}) => {
  const [openHelpTooltip, setOpenHelpTooltip] =
    useState(null);
  const [isEditingRelatedPo, setIsEditingRelatedPo] =
    useState(false);

  const [
    supplierSearchTab,
    setSupplierSearchTab,
  ] = useState(() =>
    supplierSourceToSearchTab(
      selectedSupplierObj?.supplierSource ||
        selectedSupplierObj?.SupplierSource
    )
  );

  const [supplierMarket, setSupplierMarket] =
    useState('GLOBAL');

  useEffect(
    () => {
      setOpenHelpTooltip(null);
      setIsEditingRelatedPo(false);
    },
    [currentBillId]
  );

  useEffect(() => {
    if (formData?.billPartyType !== 'shipping-account') {
      setIsEditingRelatedPo(false);
    }
  }, [formData?.billPartyType]);

  useEffect(() => {
    if (fieldErrors.poNumber && formData?.billPartyType === 'shipping-account') {
      setIsEditingRelatedPo(true);
    }
  }, [fieldErrors.poNumber, formData?.billPartyType]);

  useEffect(() => {
    const source =
      selectedSupplierObj?.supplierSource ||
      selectedSupplierObj?.SupplierSource;

    if (source) {
      setSupplierSearchTab(
        supplierSourceToSearchTab(source)
      );
    }
  }, [selectedSupplierObj]);

  const clickHelp = (id, title, label) => (
    <ClickHelpDot
      id={id}
      title={title}
      label={label}
      openHelpTooltip={openHelpTooltip}
      setOpenHelpTooltip={setOpenHelpTooltip}
    />
  );

  const liveTotalsC = computeBillTotalsCents(
    {
      billingItems:
        includedBillingItems(singleBillData),
    },
    { taxOptions }
  );

  const subTotal = fromCents(
    liveTotalsC.subTotalCents
  );

  const totalTax = fromCents(
    liveTotalsC.taxCents
  );

  const billTaxValid =
    billTaxInput !== "" &&
    Number.isFinite(Number(billTaxInput)) &&
    (
      allowNegativeBillTax ||
      Number(billTaxInput) >= 0
    );

  const overrideMode =
    billTaxManuallyEdited ||
    hasLegacyTaxDifference;

  const billTaxCents =
    overrideMode && billTaxValid
      ? Math.round(Number(billTaxInput) * 100)
      : liveTotalsC.taxCents;

  const liveBillAmount = fromCents(
    liveTotalsC.subTotalCents +
      billTaxCents
  );

  const {
    target,
    savedGroupTotal,
    projectedGroupTotal,
    variance,
    pctOfTarget,
  } = useMemo(
    () => calculatePoReconciliation({
      poAmount,
      poGroupBills,
      currentBillId,
      liveSubTotal: subTotal,
    }),
    [poAmount, poGroupBills, currentBillId, subTotal]
  );

  const billSupplierLabel =
    formData?.supplierLabel ||
    selectedSupplierObj?.Name ||
    selectedSupplierObj?.name ||
    formData?.supplierName ||
    '';

  const billDecoratorLabel =
    formData?.decoratorLabel ||
    findOptionLabel(
      decoratorOptions,
      formData?.decoratorId
    ) ||
    '';

  const billShippingAccountLabel =
    formData?.shippingAccountLabel ||
    findOptionLabel(
      shippingAccountOptions,
      formData?.shippingSelectionKey
    ) ||
    formData?.shippingAccountName ||
    '';

  const billPartyMode = formData?.billPartyType === 'other-service'
    ? 'other-service'
    : BILL_PARTY_MODES.includes(
      formData?.billPartyType
    )
      ? formData.billPartyType
      : 'supplier';

  const activeBillPartyLabel =
    billPartyMode === 'decorator'
      ? billDecoratorLabel
      : billPartyMode ===
          'shipping-account'
        ? billShippingAccountLabel
        : billSupplierLabel;

  const poSupplierLabel =
    poSupplierName ||
    formData?.poSupplierName ||
    formData?.poSupplier ||
    '';

  const poDecoratorLabel =
    poDecoratorName ||
    formData?.poDecoratorName ||
    formData?.poDecorator ||
    '';

  const poShippingLabel =
    poShippingAccountName ||
    formData?.poShippingAccountName ||
    formData?.poShippingAccount ||
    '';

  const activePoPartyLabel =
    billPartyMode === 'decorator'
      ? poDecoratorLabel
      : billPartyMode ===
          'shipping-account'
        ? poShippingLabel
        : poSupplierLabel;

  const hasPartyMismatch =
    billPartyMode !== 'shipping-account' &&
    !!activePoPartyLabel &&
    !!activeBillPartyLabel &&
    activePoPartyLabel
      .trim()
      .toLowerCase() !==
      activeBillPartyLabel
        .trim()
        .toLowerCase();

  const poPartyLabel =
    billPartyMode === 'decorator'
      ? 'PO Decorator'
      : billPartyMode ===
          'shipping-account'
        ? 'PO Shipping'
        : 'PO Supplier';

  const billPartyLabel =
    billPartyMode === 'other-service'
      ? 'Service Provider'
      : billPartyMode === 'decorator'
      ? 'Bill Decorator'
      : billPartyMode ===
          'shipping-account'
        ? 'Shipping Provider / Account'
        : 'Bill Supplier';

  const billPartyPlaceholder =
    billPartyMode === 'decorator'
      ? 'Select Decorator'
      : billPartyMode ===
          'shipping-account'
        ? 'Select Shipping Provider / Account'
        : 'Select Supplier';

  const billPartyFieldName =
    billPartyMode === 'decorator'
      ? 'decoratorId'
      : billPartyMode ===
          'shipping-account'
        ? 'shippingSelectionKey'
        : 'supplier';

  const billPartySelectedValue =
    billPartyMode === 'decorator'
      ? formData?.decoratorId
      : billPartyMode ===
          'shipping-account'
        ? formData?.shippingSelectionKey
        : formData?.supplier;

  const switchBillPartyMode = () => {
    const currentIndex =
      BILL_PARTY_MODES.indexOf(
        billPartyMode
      );

    const nextMode =
      BILL_PARTY_MODES[
        (
          currentIndex + 1
        ) %
          BILL_PARTY_MODES.length
      ];

    handleSelect(
      'billPartyType',
      nextMode
    );
  };

  const poSupplierTooltip = (
    <Box sx={{ p: 0.5 }}>
      <b>{poPartyLabel}</b> = {billPartyMode === 'shipping-account'
        ? 'optional related purchase order for freight cost tracking'
        : 'party on the purchase order'}
      {poNumber ? (
        <>
          {' '}
          <i>({poNumber})</i>
        </>
      ) : null}
      {billPartyMode === 'shipping-account' ? '.' : '. This is read-only here.'}
    </Box>
  );

  const billSupplierTooltip = (
    <Box sx={{ p: 0.5 }}>
      <b>{billPartyLabel}</b> = party on
      this bill (who actually billed you).
      Usually matches the PO party, but
      can differ (subs, drop-ship,
      corrections).
    </Box>
  );

  const suppliersMismatchTooltip = (
    <Box sx={{ p: 0.5 }}>
      <b>{poPartyLabel}</b> and{' '}
      <b>{billPartyLabel}</b> do not
      match. Confirm this is expected.
    </Box>
  );

  const defaultTaxLabel =
    findOptionLabel(
      taxOptions,
      formData?.defaultTax
    );

  const nextBillPartyLabel =
    billPartyMode === 'supplier'
      ? 'Bill Decorator'
      : billPartyMode === 'decorator'
        ? 'Shipping'
        : 'Bill Supplier';

  return (
    <Grid
      className="billing-form"
      container
      gap={5}
    >
      <Grid item xs={6}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns:
              '1fr 1fr',
            gap: 2,
            mb: 0,
          }}
        >
          <Box
            className={
              styles.selectedDetails
            }
            sx={{ p: 1.25 }}
          >
            <Typography
              className={styles.title}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              {poPartyLabel}{' '}
              <InfoDot
                title={
                  poSupplierTooltip
                }
              />
            </Typography>

            <Box
              data-billing-field={billPartyMode === 'shipping-account' || (billPartyMode === 'supplier' && !poNumber) ? 'poNumber' : undefined}
              sx={{ mt: 0.5 }}
            >
              {(billPartyMode === 'shipping-account' || (billPartyMode === 'supplier' && !poNumber && !isPoOptionalSupplier)) && isEditingRelatedPo ? (
                <CustomDropdown
                  name="relatedPoNumber"
                  options={[
                    ...(billPartyMode === 'shipping-account' ? [{ id: '', name: 'N/A — Project-level freight' }] : []),
                    ...purchaseOrderOptions
                      .filter((po) => po?.poNumber)
                      .map((po) => ({ id: String(po.poNumber), name: String(po.poNumber) })),
                  ]}
                  handleSelect={(event) => {
                    handleSelect(event, 'relatedPoNumber');
                    setIsEditingRelatedPo(false);
                  }}
                  selectedValue={formData?.relatedPoNumber || ''}
                  minWidth="150px"
                  error={Boolean(fieldErrors.poNumber)}
                  helperText={fieldErrors.poNumber}
                />
              ) : (
                <Typography
                  className={styles.values}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  {billPartyMode === 'shipping-account'
                    ? (formData?.relatedPoNumber || 'N/A')
                    : (billPartyMode === 'supplier' && !poNumber ? (isPoOptionalSupplier ? '—' : (formData?.relatedPoNumber || '—')) : (activePoPartyLabel || '—'))}

                  {(billPartyMode === 'shipping-account' || (billPartyMode === 'supplier' && !poNumber && !isPoOptionalSupplier && !poOptionalSupplierLoading)) ? (
                    <Tooltip
                      title={formData?.relatedPoNumber
                        ? 'Change related purchase order'
                        : 'Attach to purchase order'}
                      arrow
                    >
                      <IconButton
                        size="small"
                        aria-label={formData?.relatedPoNumber
                          ? 'Change related purchase order'
                          : 'Attach to purchase order'}
                        onClick={() => setIsEditingRelatedPo(true)}
                        sx={{ p: 0.25 }}
                      >
                        <EditOutlinedIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  ) : null}

                  {hasPartyMismatch && (
                <Tooltip
                  title={
                    suppliersMismatchTooltip
                  }
                  arrow
                >
                  <Chip
                    size="small"
                    label="!="
                    color="warning"
                    variant="outlined"
                  />
                </Tooltip>
                  )}
                </Typography>
              )}
              {billPartyMode === 'shipping-account' && fieldErrors.poNumber && !isEditingRelatedPo ? (
                <Typography sx={{ fontSize: 12, color: '#d32f2f' }}>
                  {fieldErrors.poNumber}
                </Typography>
              ) : null}
            </Box>
          </Box>

          <Box
            className={
              styles.selectedDetails
            }
            sx={{ p: 1.25 }}
          >
            <Typography
              className={styles.title}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              {billPartyLabel}

              {clickHelp(
                'billParty',
                billSupplierTooltip,
                billPartyLabel
              )}

              <Tooltip
                title={`Switch to ${nextBillPartyLabel}`}
                arrow
              >
                <IconButton
                  disabled={billPartyMode === 'other-service'}
                  size="small"
                  aria-label={`Switch to ${nextBillPartyLabel}`}
                  onClick={
                    switchBillPartyMode
                  }
                  sx={{ ml: 0.25 }}
                >
                  <SwapHorizIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </Typography>

            <Box data-billing-field={billPartyFieldName}>
              {billPartyMode === 'other-service' ? (
                <Typography className={styles.values} sx={{ mt: 1 }}>
                  {formData?.otherServiceParty?.partyName || formData?.supplierName || 'Other Service'}
                </Typography>
              ) : billPartyMode ===
              'supplier' ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection:
                      'column',
                    gap: 0.75,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      color:
                        'text.secondary',
                    }}
                  >
                    Supplier Source
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems:
                        'center',
                      gap: 0.75,
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        p: 0.35,
                        border:
                          '1px solid',
                        borderColor:
                          'divider',
                        borderRadius: 2,
                        backgroundColor:
                          'grey.50',
                        minWidth: 0,
                      }}
                    >
                      {[
                        {
                          value:
                            'core',
                          label:
                            'Internal Supplier',
                        },
                        {
                          value:
                            'all',
                          label:
                            'Supplier',
                        },
                      ].map(
                        (option) => {
                          const active =
                            supplierSearchTab ===
                            option.value;

                          return (
                            <Box
                              key={
                                option.value
                              }
                              component="button"
                              type="button"
                              onClick={() => {
                                if (
                                  active
                                ) {
                                  return;
                                }

                                setSupplierSearchTab(
                                  option.value
                                );

                                onSelectSupplier?.(
                                  null
                                );
                              }}
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                border: 0,
                                px: 0.75,
                                py: 0.6,
                                borderRadius: 1.5,
                                cursor: active
                                  ? 'default'
                                  : 'pointer',
                                font: 'inherit',
                                fontSize: 11.5,
                                fontWeight:
                                  active
                                    ? 700
                                    : 600,
                                lineHeight: 1.2,
                                whiteSpace:
                                  'nowrap',
                                color: active
                                  ? 'primary.main'
                                  : 'text.secondary',
                                backgroundColor:
                                  active
                                    ? 'background.paper'
                                    : 'transparent',
                                boxShadow: active
                                  ? '0 1px 3px rgba(0,0,0,0.08)'
                                  : 'none',
                                transition:
                                  'all 150ms ease',
                                '&:hover':
                                  {
                                    backgroundColor:
                                      active
                                        ? 'background.paper'
                                        : 'action.hover',
                                  },
                                '&:focus-visible':
                                  {
                                    outline:
                                      '2px solid',
                                    outlineColor:
                                      'primary.main',
                                    outlineOffset: 1,
                                  },
                              }}
                            >
                              {
                                option.label
                              }
                            </Box>
                          );
                        }
                      )}
                    </Box>

                    {supplierSearchTab ===
                    'all' ? (
                      <Box
                        sx={{
                          display:
                            'flex',
                          alignItems:
                            'center',
                          gap: 0.4,
                          flexShrink: 0,
                        }}
                      >
                        <Tooltip
                          title="Canada suppliers"
                          arrow
                        >
                          <Box
                            component="button"
                            type="button"
                            onClick={() =>
                              setSupplierMarket(
                                'CANALL'
                              )
                            }
                            sx={{
                              width: 28,
                              height: 28,
                              p: 0,
                              borderRadius: 1.5,
                              border:
                                '1px solid',
                              borderColor:
                                supplierMarket ===
                                'CANALL'
                                  ? 'primary.main'
                                  : 'divider',
                              backgroundColor:
                                supplierMarket ===
                                'CANALL'
                                  ? 'primary.main'
                                  : 'background.paper',
                              display:
                                'flex',
                              alignItems:
                                'center',
                              justifyContent:
                                'center',
                              cursor:
                                'pointer',
                              transition:
                                'all 150ms ease',
                              '&:hover':
                                {
                                  borderColor:
                                    'primary.main',
                                },
                            }}
                          >
                            <img
                              src="https://flagcdn.com/ca.svg"
                              alt="Canada"
                              style={{
                                width: 16,
                                height: 11,
                                objectFit:
                                  'cover',
                                borderRadius: 2,
                              }}
                            />
                          </Box>
                        </Tooltip>

                        <Tooltip
                          title="All suppliers"
                          arrow
                        >
                          <Box
                            component="button"
                            type="button"
                            onClick={() =>
                              setSupplierMarket(
                                'GLOBAL'
                              )
                            }
                            sx={{
                              width: 28,
                              height: 28,
                              p: 0,
                              borderRadius: 1.5,
                              border:
                                '1px solid',
                              borderColor:
                                supplierMarket ===
                                'GLOBAL'
                                  ? 'primary.main'
                                  : 'divider',
                              backgroundColor:
                                supplierMarket ===
                                'GLOBAL'
                                  ? 'primary.main'
                                  : 'background.paper',
                              color:
                                supplierMarket ===
                                'GLOBAL'
                                  ? 'primary.contrastText'
                                  : 'text.secondary',
                              display:
                                'flex',
                              alignItems:
                                'center',
                              justifyContent:
                                'center',
                              cursor:
                                'pointer',
                              transition:
                                'all 150ms ease',
                              '&:hover':
                                {
                                  borderColor:
                                    'primary.main',
                                },
                            }}
                          >
                            <PublicIcon
                              sx={{
                                fontSize: 16,
                              }}
                            />
                          </Box>
                        </Tooltip>
                      </Box>
                    ) : null}
                  </Box>

                  <SupplierSearchSelectAsync
                    key={
                      supplierSearchTab
                    }
                    instanceUniqueId={
                      instanceUniqueId
                    }
                    value={
                      formData?.supplier ||
                      null
                    }
                    selectedSupplierOption={
                      selectedSupplierObj
                    }
                    onSelectSupplier={(
                      supplier
                    ) => {
                      if (!supplier) {
                        onSelectSupplier?.(
                          null
                        );
                        return;
                      }

                      onSelectSupplier?.(
                        applyBillingSupplierSearchSelection(
                          supplier,
                          supplierSearchTab
                        )
                      );
                    }}
                    label="Bill Supplier"
                    showLabel={false}
                    inputId="bill-supplier-search"
                    tab={
                      supplierSearchTab
                    }
                    searchMode={
                      supplierSearchTab
                    }
                    market={
                      supplierMarket
                    }
                    placeholder={
                      supplierSearchTab ===
                      'core'
                        ? 'Search internal suppliers...'
                        : billPartyPlaceholder
                    }
                  />
                  {fieldErrors.supplier ? (
                    <Typography sx={{ fontSize: 12, color: '#d32f2f' }}>
                      {fieldErrors.supplier}
                    </Typography>
                  ) : null}
                </Box>
              ) : (
                <CustomDropdown
                  name={
                    billPartyFieldName
                  }
                  options={
                    billPartyMode ===
                    'decorator'
                      ? decoratorOptions
                      : [
                          { id: '__providers', name: 'Shipping Providers', disabled: true },
                          ...shippingAccountOptions.filter((option) => option.sourceType === 'courier'),
                          { id: '__accounts', name: 'Shipping Accounts', disabled: true },
                          ...shippingAccountOptions.filter((option) => option.sourceType === 'shipping-account'),
                        ]
                  }
                  handleSelect={
                    handleSelect
                  }
                  minWidth={"150px"}
                  selectedValue={
                    billPartySelectedValue
                  }
                  placeholder={
                    billPartyPlaceholder
                  }
                  label={billPartyMode === 'shipping-account' ? 'Shipping Provider / Account' : undefined}
                  error={Boolean(fieldErrors[billPartyFieldName])}
                  helperText={fieldErrors[billPartyFieldName]}
                />
              )}
            </Box>
          </Box>
        </Box>

        <Box
          className="invoice-general-details"
          xs={2}
        >
          {isMinimalBillEntryActive ? (
            <Alert
              severity="info"
              sx={{ mt: 1 }}
            >
              {
                minimalBillEntryMessage
              }
            </Alert>
          ) : null}

          {!isMinimalBillEntryActive ? (
            <>
              <Box
                className="field"
                sx={{
                  marginTop:
                    '5px !important',
                }}
                data-billing-field="invoice"
              >
                <CustomTextField
                  name="invoice"
                  value={
                    formData.invoice
                  }
                  isRequired
                  placeholder="Enter vendor invoice number"
                  handleChange={
                    handleInputChange
                  }
                  label="Invoice #"
                  error={Boolean(fieldErrors.invoice)}
                  helperText={fieldErrors.invoice}
                />
              </Box>

              <Box className="field" data-billing-field="invoiceDate">
                <CustomDatePicker
                  isRequired
                  label="Invoice Date"
                  labelAdornment={clickHelp(
                    'invoiceDate',
                    "The date printed on the vendor's invoice.",
                    'Invoice Date'
                  )}
                  value={
                    formData.invoiceDate
                  }
                  onChange={(date) =>
                    handleDateChange(
                      date,
                      'invoiceDate'
                    )
                  }
                  error={Boolean(fieldErrors.invoiceDate)}
                  helperText={fieldErrors.invoiceDate}
                />
              </Box>

              <Box className="field" data-billing-field="dueDate">
                <CustomDatePicker
                  isRequired
                  label="Due Date"
                  labelAdornment={clickHelp(
                    'dueDate',
                    'Payment due date based on vendor terms.',
                    'Due Date'
                  )}
                  value={
                    formData.dueDate
                  }
                  onChange={(date) =>
                    handleDateChange(
                      date,
                      'dueDate'
                    )
                  }
                  error={Boolean(fieldErrors.dueDate)}
                  helperText={fieldErrors.dueDate}
                />
              </Box>

              <Box className="field">
                <TextArea
                  handleInputChange={
                    handleInputChange
                  }
                  value={
                    formData.notes
                  }
                  name="notes"
                  label="Notes"
                  labelAdornment={clickHelp(
                    'notes',
                    'Optional internal notes for this bill.',
                    'Notes'
                  )}
                />
              </Box>
            </>
          ) : null}
        </Box>
      </Grid>

      <Grid item xs={5}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns:
              '1fr 1fr 1fr',
            gap: 2,
            mb: 0,
          }}
        >
          <Tooltip
            title="Booked (Target) from POs for this PO group."
            arrow
          >
            <Box
              className={
                styles.selectedDetails
              }
            >
              <Typography
                className={
                  styles.title
                }
              >
                PO Amount (Target)
              </Typography>

              <Typography
                className={
                  styles.values
                }
              >
                {formatCAD(target)}
              </Typography>
            </Box>
          </Tooltip>

          <Tooltip
            title="Sum of all saved bill amounts under this PO (before your current edits)."
            arrow
          >
            <Box
              className={
                styles.selectedDetails
              }
            >
              <Typography
                className={
                  styles.title
                }
              >
                PO Billed (Saved)
              </Typography>

              <Typography
                className={
                  styles.values
                }
              >
                {formatCAD(
                  savedGroupTotal
                )}
              </Typography>
            </Box>
          </Tooltip>

          <Tooltip
            title={
              <Box sx={{ p: 0.5 }}>
                <b>
                  Projected PO Billed
                </b>{' '}
                = (Saved total - this
                bill's saved amount) +
                this bill's live amount.
                <br />
                Lets you see where the
                PO lands if you save
                these changes.
              </Box>
            }
            arrow
          >
            <Box
              className={
                styles.selectedDetails
              }
            >
              <Typography
                className={
                  styles.title
                }
              >
                Projected After Save
              </Typography>

              <Typography
                className={
                  styles.values
                }
                sx={{
                  color:
                    projectedGroupTotal >
                    target
                      ? '#d32f2f'
                      : '#2e7d32',
                }}
              >
                {formatCAD(
                  projectedGroupTotal
                )}
              </Typography>
            </Box>
          </Tooltip>
        </Box>

        <Tooltip
          title={
            <Box sx={{ p: 0.5 }}>
              <b>Variance</b> =
              Projected PO Billed - PO
              Amount (Target).
              <br />

              {target > 0 ? (
                <>
                  Currently{' '}
                  {pctOfTarget.toFixed(
                    1
                  )}
                  % of target.
                </>
              ) : (
                <>
                  No target entered yet.
                </>
              )}
            </Box>
          }
          arrow
        >
          <Box
            className={`${styles.selectedDetails} ${styles.marginbottom}`}
          >
            <Typography
              className={styles.title}
            >
              Variance{' '}
              {target > 0
                ? `(${pctOfTarget.toFixed(
                    1
                  )}% of target)`
                : ''}
            </Typography>

            <Typography
              className={
                styles.values
              }
              sx={{
                color:
                  variance > 0
                    ? '#d32f2f'
                    : '#2e7d32',
              }}
              title={
                variance > 0
                  ? 'Over target'
                  : 'Under target'
              }
            >
              {formatCAD(variance)}
            </Typography>
          </Box>
        </Tooltip>

        <Box className="field" data-billing-field="defaultTax">
          <CustomDropdown
            name="defaultTax"
            options={taxOptions}
            handleSelect={
              handleSelect
            }
            minWidth={"150px"}
            selectedValue={
              formData.defaultTax
            }
            placeholder="Select Default Tax"
            label="Default Tax"
            labelAdornment={clickHelp(
              'defaultTax',
              allowDetailedItemTaxCustomization
                ? 'Default tax for new bill items. Existing line tax selections can be overridden per line.'
                : 'Default tax for this bill. Changing it recalculates all included bill items.',
              'Default Tax'
            )}
            error={Boolean(fieldErrors.defaultTax)}
            helperText={fieldErrors.defaultTax}
          />
        </Box>

        <Tooltip
          title="Sum of all line item amounts before tax."
          arrow
        >
          <Box
            className={`${styles.selectedDetails} ${styles.marginbottom}`}
          >
            <Typography
              className={
                styles.title
              }
            >
              Subtotal
            </Typography>

            <Typography
              className={
                styles.values
              }
            >
              {formatCAD(subTotal)}
            </Typography>
          </Box>
        </Tooltip>

        <Tooltip
          title="Tax calculated from current bill items."
          arrow
        >
          <Box
            className={
              styles.selectedDetails
            }
          >
            <Typography
              className={
                styles.title
              }
            >
              Calculated Tax
            </Typography>

            <Typography
              className={
                styles.values
              }
            >
              {formatCAD(totalTax)}
            </Typography>
          </Box>
        </Tooltip>

        <Box
          className={
            styles.selectedDetails
          }
          sx={{
            display: 'block',
          }}
          data-billing-field="billTax"
        >
          <Typography
            className={styles.title}
          >
            Bill Tax
          </Typography>

          {overrideMode ? (
            <>
              <TextField
                size="small"
                type="number"
                inputProps={{
                  min: allowNegativeBillTax
                    ? undefined
                    : 0,
                  step: '0.01',
                  'aria-label':
                    'Bill Tax',
                }}
                value={billTaxInput}
                error={!billTaxValid}
                helperText={
                  !billTaxValid
                    ? allowNegativeBillTax
                      ? 'Enter a valid tax amount.'
                      : 'Enter a non-negative tax amount.'
                    : billTaxManuallyEdited
                      ? `Manually adjusted from ${formatCAD(
                          totalTax
                        )}`
                      : hasLegacyTaxDifference
                        ? 'Saved bill tax differs from the current item calculation.'
                        : 'Tax overridden'
                }
                onChange={(event) =>
                  onBillTaxInputChange(
                    event.target.value
                  )
                }
                onBlur={() =>
                  billTaxValid &&
                  setBillTaxInput(
                    Number(
                      billTaxInput
                    ).toFixed(2)
                  )
                }
              />

              <Button
                size="small"
                onClick={
                  onResetBillTaxInput
                }
              >
                Reset to calculated tax
              </Button>
            </>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Typography
                className={
                  styles.values
                }
              >
                {formatCAD(totalTax)}
              </Typography>

              <Tooltip
                title="Override bill tax"
                arrow
              >
                <IconButton
                  size="small"
                  aria-label="Override bill tax"
                  onClick={() =>
                    onBillTaxInputChange(
                      totalTax.toFixed(2)
                    )
                  }
                >
                  <EditOutlinedIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>

        <Tooltip
          title="Subtotal + Total Tax (authoritative from line items for this bill)."
          arrow
        >
          <Box
            className={
              styles.selectedDetails
            }
          >
            <Typography
              className={
                styles.title
              }
            >
              This Bill
            </Typography>

            <Typography
              className={
                styles.values
              }
            >
              {formatCAD(
                liveBillAmount
              )}
            </Typography>
          </Box>
        </Tooltip>
      </Grid>

      <Grid container gap={5}>
        {fieldErrors.billingItems ? (
          <Alert severity="error" sx={{ width: '100%' }} data-billing-field="billingItems">
            {fieldErrors.billingItems}
          </Alert>
        ) : null}
        <BillingItemsTable
          financeCodes={
            financeCodes
          }
          taxOptions={taxOptions}
          allowDetailedItemTaxCustomization={allowDetailedItemTaxCustomization}
          defaultTax={
            formData.defaultTax
          }
          defaultTaxLabel={
            defaultTaxLabel
          }
          setSingleBillData={
            setSingleBillData
          }
          tabId={tabId}
          handleSelect={
            handleSelect
          }
          singleBillData={
            singleBillData
          }
          handleInputChange={
            handleInputChange
          }
        />
      </Grid>
    </Grid>
  );
};

export default MainBillingForm;
